import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { RightsholderContact, RightsholderContacts } from '@Entities/rightsholder/rightsholder-contact'
import {
    createRightsholderContact,
    deleteRightsholderContact,
    editRightsholderContact,
    getRightsholderContactById,
    getRightsholderContacts,
} from '@App/api/routes/rightsholder/contact'
import RootStore from '@App/stores/root-store'
import { flattenObject } from '@App/helpers/flatten-object'
import { RightsholderContactTypes } from '@App/enum/rightsholder-contact-types'

export default class ContactStore {
    private rootStore: RootStore

    private _rightsholderContact: RightsholderContact | null

    private _rightsholderContacts: RightsholderContacts

    constructor(rootStore: RootStore) {
        makeObservable<ContactStore, '_rightsholderContact' | '_rightsholderContacts' | 'updateContactList'>(this, {
            _rightsholderContact: observable,
            _rightsholderContacts: observable,
            rightsholderHasPublicContact: computed,
            setRightsholderContact: action,
            updateContactList: action,
        })

        this.rootStore = rootStore
    }

    get rightsholderContact() {
        return this._rightsholderContact
    }

    get rightsholderContacts() {
        return this._rightsholderContacts
    }

    get rightsholderHasPublicContact(): boolean {
        return !!this._rightsholderContacts?.data.some(
            (contact) =>
                contact.rightsholderContactTypeId === RightsholderContactTypes.PUBLIC &&
                this._rightsholderContact?.id !== contact.id
        )
    }

    public setRightsholderContact = (rightsholderContact: RightsholderContact | null) => {
        this._rightsholderContact = rightsholderContact
    }

    public loadRightsholderContacts = async (): Promise<void> => {
        const response = await getRightsholderContacts(this.rootStore.rightsholderStore.currentRightsholderId)

        const mappedResponse = response.data?.data

        runInAction(() => {
            this._rightsholderContacts = { count: response.data.count, data: mappedResponse }
        })
    }

    public loadRightsholderContactById = async (contactId: string): Promise<void> => {
        // Attempt to find already-loaded contact first
        if (this._rightsholderContacts) {
            const contactList: RightsholderContacts = this._rightsholderContacts
            const matchingContact = contactList.data.find((contact: RightsholderContact) => contact.id === contactId)
            if (matchingContact) {
                this.setRightsholderContact(matchingContact)
                return Promise.resolve()
            }
        }

        const response = await getRightsholderContactById(
            this.rootStore.rightsholderStore.currentRightsholderId,
            contactId
        )
        this.setRightsholderContact(flattenObject(response.data))
    }

    public editRightsholderContact = async (contact: RightsholderContact) => {
        const updatedContactResponse = await editRightsholderContact(contact)
        const updatedContact = updatedContactResponse.data
        this.setRightsholderContact(updatedContact)
        const rightsholderContacts = this.rightsholderContacts
        if (rightsholderContacts) {
            const contactList = rightsholderContacts.data
            const oldContactIndex = contactList.findIndex((contactFromList) => contactFromList.id === contact.id)
            if (oldContactIndex) {
                contactList[oldContactIndex] = updatedContact
                this.updateContactList(() => contactList)
            } else {
                await this.loadRightsholderContacts()
            }
        } else {
            await this.loadRightsholderContacts()
        }
    }

    public createRightsholderContact = async (contact: RightsholderContact) => {
        contact.ipId = this.rootStore.rightsholderStore.currentRightsholderId
        const newContactResponse = await createRightsholderContact(contact)
        const newContact = newContactResponse.data
        this.setRightsholderContact(newContact)
        const rightsholderContacts = this.rightsholderContacts
        if (rightsholderContacts) {
            const contactList: RightsholderContact[] = rightsholderContacts.data
            contactList.push(newContact)
            this.updateContactList(() => contactList)
        } else {
            await this.loadRightsholderContacts()
        }
    }

    public deleteRightsholderContact = async (rightsholderId: number, contactId: string): Promise<void> =>
        deleteRightsholderContact(rightsholderId, contactId)
            .then(async () => {
                if (this.rightsholderContacts) {
                    const contactList: RightsholderContacts = this.rightsholderContacts
                    this.updateContactList(() => contactList.data.filter((contact) => contact.id !== contactId))
                }
                return Promise.resolve()
            })
            .catch((error) => Promise.reject(error))

    private updateContactList(newContactListFn: () => RightsholderContact[]) {
        const newContactList = newContactListFn()
        if (newContactList) {
            this._rightsholderContacts = {
                count: newContactList.length,
                data: newContactList,
            }
        }
    }
}
